import moment from "moment";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { Space, Button, Table, Tag, message, Dropdown, Modal, Tooltip } from "antd";
import {
  FileProtectOutlined,
  FileAddOutlined,
  DownOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";

import sdk from "@/sdk";
import { GetOrderListsQuery, OrderListStatus } from "@/graphql";
import { useCanEdit } from "@/hooks/usePermission";

import { useOrderLists } from "./useOrderLists";
import { useOrderListDetail } from "./useOrderListDetail";
import { useDownloadOrderList } from "./useDownloadOrderList";

function ReleaseHoldsButton({ orderListId }: { orderListId: string }) {
  const [confirming, setConfirming] = useState(false);
  const { mutate, isLoading } = useMutation({
    mutationFn: () => sdk.releaseHoldAmountForOrderList({ orderListId }),
    onSuccess: ({ releaseHoldAmountForOrderList: count }) => {
      message.success(
        `${count} хэрэглэгчийн худалдан авалтаас үлдсэн дүн буцаагдлаа`
      );
      setConfirming(false);
    },
    onError: (err: any) => {
      message.error(err.response?.errors?.[0]?.message ?? "Алдаа гарлаа");
    },
  });

  return (
    <>
      <Tooltip title="Худалдан авалтаас үлдсэн дүн буцаах">
        <Button
          type="primary"
          shape="circle"
          danger
          loading={isLoading}
          icon={<UnlockOutlined />}
          onClick={() => setConfirming(true)}
        />
      </Tooltip>
      <Modal
        title="Худалдан авалтаас үлдсэн дүн буцаах"
        open={confirming}
        confirmLoading={isLoading}
        okText="Буцаах"
        cancelText="Болих"
        okButtonProps={{ danger: true }}
        onOk={() => mutate()}
        onCancel={() => setConfirming(false)}
      >
        Энэ захиалгын хуудас доторх бүх хэрэглэгчийн худалдан авалтаас үлдсэн дүнг
        буцаахдаа итгэлтэй байна уу?
      </Modal>
    </>
  );
}

export type OrderListItem =
  GetOrderListsQuery["orderLists"]["orderLists"][number];

function DownloadOrderListButton({ orderListId }: { orderListId: string }) {
  const { loading, startDownload } = useDownloadOrderList(orderListId);
  const {
    loading: generating,
    generateOrderListDetail,
    generateOrderListOrders,
    regenerateOrderListFile,
    generateOrderListDetailPerUser,
    data: url,
    orderData: urlOrder,
    perUserData: urlPerUser,
  } = useOrderListDetail(orderListId);

  const menuItems = [
    {
      key: "download",
      label: "Татаж авах",
      onClick: () => startDownload(),
    },
    {
      key: "request",
      label: "Файлын линк илгээх",
      onClick: () => generateOrderListDetail(),
    },
    {
      key: "regenerate",
      label: "Файл шинээр үүсгэх",
      onClick: () => regenerateOrderListFile(),
    },
    {
      key: "orders",
      label: "Цэнэглэлт",
      onClick: () => generateOrderListOrders(),
    },
    {
      key: "perUser",
      label: "Хэрэглэгч тус бүрээр татах",
      onClick: () => generateOrderListDetailPerUser(),
    },
    ...(url
      ? [
          {
            key: "downloadDetail",
            label: (
              <a href={url} download>
                Дэлгэрэнгүй файл татах
              </a>
            ),
          },
        ]
      : []),
    ...(urlOrder
      ? [
          {
            key: "downloadOrders",
            label: (
              <a href={urlOrder} download>
                Цэнэглэлтийн файл татах
              </a>
            ),
          },
        ]
      : []),
    ...(urlPerUser
      ? [
          {
            key: "downloadPerUser",
            label: (
              <a href={urlPerUser} download>
                Хэрэглэгч тус бүрийн файл татах
              </a>
            ),
          },
        ]
      : []),
  ];

  return (
    <Dropdown.Button
      disabled={loading || generating}
      loading={loading || generating}
      menu={{ items: menuItems }}
      icon={<DownOutlined />}
      onClick={() => startDownload()}
    >
      {generating ? "Түр хүлээнэ үү..." : "Татаж авах"}
    </Dropdown.Button>
  );
}

export const OrderListWidget = ({ status }: { status: OrderListStatus }) => {
  const history = useHistory();
  const { loading, data, setPage } = useOrderLists({ status });
  const canEdit = useCanEdit("orderlists");

  return (
    <>
      <div className="page-header">
        <h3>Захиалгын хуудсууд</h3>
        <div style={{ display: "flex" }}>
          {canEdit && (
            <Space>
              <Button
                loading={loading}
                disabled={loading}
                style={{ marginTop: 10 }}
                type="primary"
                icon={<FileAddOutlined />}
                onClick={() => history.push("orderlists/create")}
              >
                Захиалгын хуудас үүсгэх
              </Button>
            </Space>
          )}
        </div>
      </div>
      <div className="page-body">
        <div className="page-table">
          <Table
            scroll={{ x: 900 }}
            dataSource={data?.orderLists ?? []}
            size="small"
            loading={loading}
            pagination={{
              pageSize: 30,
              total: Number(data?.totalPages) * 30,
            }}
            onChange={(pagination) => setPage(Number(pagination?.current) - 1)}
            bordered
            columns={[
              {
                title: "№",
                dataIndex: "number",
                key: "number",
                render: (_, __, index) => <span>{index + 1}</span>,
              },
              {
                title: "Төлөв",
                dataIndex: "status",
                key: "status",
                render: (text) => <Tag>{text}</Tag>,
              },
              {
                title: "Нийт захиалгын тоо",
                dataIndex: "totalPurchases",
                key: "totalPurchases",
                render: (_, record) => (
                  <span>{record?.totalPurchases ?? 0}</span>
                ),
              },
              {
                title: "Үүсгэсэн огноо",
                dataIndex: "createdAt",
                key: "createdAt",
                render: (text) => (
                  <span>{moment(text).format("YYYY/MM/DD HH:mm:ss")}</span>
                ),
              },
              {
                title: "Шинэчлэгдсэн огноо",
                dataIndex: "updatedAt",
                key: "updatedAt",
                render: (text) => (
                  <span>
                    {text ? moment(text).format("YYYY/MM/DD HH:mm:ss") : "-"}
                  </span>
                ),
              },
              {
                title: "",
                dataIndex: "download",
                key: "download",
                render: (_, record) =>
                  record._id && (
                    <DownloadOrderListButton orderListId={record._id} />
                  ),
              },
              {
                title: "",
                dataIndex: "button",
                key: "button",
                fixed: "right",
                width: "45px",
                render: (_, record) => {
                  if (!canEdit) return null;
                  return (
                    <Space>
                      <Button
                        type="primary"
                        shape="circle"
                        onClick={() => {
                          if (record.status === OrderListStatus.Done) {
                            message.warn("Биелэгдсэн захиалгын хуудас байна");
                          } else {
                            history.push(`/orderlist/${record._id}`);
                          }
                        }}
                        icon={<FileProtectOutlined />}
                      />
                    </Space>
                  );
                },
              },
              {
                title: "",
                dataIndex: "releaseHolds",
                key: "releaseHolds",
                fixed: "right",
                width: "45px",
                render: (_, record) =>
                  canEdit &&
                  record._id &&
                  record.status === OrderListStatus.Done ? (
                    <ReleaseHoldsButton orderListId={record._id} />
                  ) : null,
              },
            ]}
          />
        </div>
      </div>
    </>
  );
};
